import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';

import { storeName as projectModelStore } from './project.model';
import { storeName as layoutModelStore } from './layout.model';
import { RootModel } from '../models';
import { storeName as newsEntityStore } from '../entities/news.entity';
import { buildHashUrl } from '../../utils';
import {
  ABOUT_ITEMS,
  SHARE_LINK_KEY,
  MODULE_PAGE_LINK,
  LanguageTypes,
  NEWS_LANGUAGE_PATH,
  ABOUT_LANGUAGE_PATH,
  NEWS_DETAIL_PAGE_LINK,
} from '../../commons/constants';

export const storeName = 'aboutModel';

type AboutModelTypes = {
  selectedItemId: string;
  activeContent: string;
  newsId: number;
  newsKey: string;
  newsPageNumber: number;
  isLastNews: boolean;
  initialNews: boolean;
  backFlowKey: string;
  cooperatorCode: string;
  openContent: string;
};

const initialState: AboutModelTypes = {
  selectedItemId: null,
  activeContent: null,
  newsId: null,
  newsKey: null,
  newsPageNumber: null,
  isLastNews: null,
  initialNews: null,
  backFlowKey: null,
  cooperatorCode: null,
  openContent: null,
};

export const aboutModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setSelectedItemId: produce((state, payload: string) => {
      state.selectedItemId = payload;
    }),
    setBackFlowKey: produce((state, payload: string) => {
      state.backFlowKey = payload;
    }),
    setActiveContent: produce((state, payload: string) => {
      state.activeContent = payload;
    }),

    setDetailNew: produce((state, payload) => {
      const { newsId, newsKey } = payload;
      state.newsId = newsId;
      state.newsKey = newsKey;
    }),

    resetDetailNew: produce((state, payload) => {
      state.newsId = null;
      state.newsKey = null;
    }),
    setNewsPageNumber: produce((state, payload: number) => {
      state.newsPageNumber = payload;
    }),
    setIsLastNews: produce((state, payload: boolean) => {
      state.isLastNews = payload;
    }),
    setInitialNews: produce((state, payload: boolean) => {
      state.initialNews = payload;
    }),

    setCooperatorCode: produce((state, payload: string) => {
      state.cooperatorCode = payload;
    }),
    setOpenContent: produce((state, payload: string) => {
      if (!payload) {
        state.openContent = initialState.openContent;
        return;
      }

      state.openContent = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async setActiveAboutItem({ key, history }, state) {
      const language: LanguageTypes = state[layoutModelStore].locale;

      if (key === ABOUT_ITEMS.news.key) {
        const link = dispatch[projectModelStore]
          .getHomePageLink({ link: MODULE_PAGE_LINK })
          .replace(':projectKey', state[projectModelStore].projectKey)
          .replace(':activeKey', NEWS_LANGUAGE_PATH[language.locale.toUpperCase()]);

        if (history && !!link) history.push(link);
        return;
      }

      this.updateHashParams({ activeContent: key });
    },

    setDetailNews({ newsKey, history }, state) {
      const language: LanguageTypes = state[layoutModelStore].locale;

      const link = dispatch[projectModelStore]
        .getHomePageLink({ link: NEWS_DETAIL_PAGE_LINK })
        .replace(':languagePath', NEWS_LANGUAGE_PATH[language.locale.toUpperCase()])
        .replace(':projectKey', state[projectModelStore].projectKey)
        .replace(':itemKey', newsKey);

      if (history && !!link) history.push(link);
    },

    goBackAboutPage({ history }, state) {
      const language: LanguageTypes = state[layoutModelStore].locale;

      const link = dispatch[projectModelStore]
        .getHomePageLink({ link: MODULE_PAGE_LINK })
        .replace(':projectKey', state[projectModelStore].projectKey)
        .replace(':activeKey', ABOUT_LANGUAGE_PATH[language.locale.toUpperCase()]);

      if (history && !!link) history.push(link);
      this.resetDetailNew();
    },

    async setNewsDetailKey(payload, state) {
      const { newsKey } = payload;
      dispatch[layoutModelStore].setLoading(true);
      try {
        const news = await dispatch[newsEntityStore].loadByKey({ newsKey });
        if (news) {
          this.setDetailNew({ newsId: news.id, newsKey: news.code });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch[layoutModelStore].setLoading(false);
      }
    },

    updateHashParams(payload, state) {
      const { cooperatorCode, activeContent = state[storeName].activeContent } = payload;

      // create new url params object
      const urlParams = {};
      if (activeContent) {
        urlParams[SHARE_LINK_KEY.ABOUT_ITEM] = activeContent;
      }

      if (cooperatorCode) {
        urlParams[SHARE_LINK_KEY.COOPERATOR_DETAIL] = cooperatorCode;
      }

      buildHashUrl(urlParams);
    },

    async getNewsByProjectKey(payload, state) {
      const { projectKey, page = 0 } = payload;
      const projectKeyRedux = state.projectModel.projectKey;
      const { newsIds } = state.projectModel;
      dispatch[layoutModelStore].setLoading(true);
      try {
        const resp = await dispatch[newsEntityStore].loadByProjectKey({
          projectKey,
          page,
        });
        if (resp) {
          let dataNewsIds = newsIds || [];
          // set list ids in project model
          const listNewsIds: number[] = resp.content.map((item) => item.id);
          if (projectKeyRedux !== projectKey) {
            dataNewsIds = listNewsIds;
          } else {
            dataNewsIds = dataNewsIds.concat(listNewsIds);
          }
          dispatch[projectModelStore].setNewsIds(dataNewsIds);
          this.setNewsPageNumber(resp.number);

          if (resp.last) {
            this.setIsLastNews(true);
          }
        }
        this.setActiveContent(ABOUT_ITEMS.news.key);
      } catch (error) {
        console.error(error);
      } finally {
        dispatch[layoutModelStore].setLoading(false);
        dispatch[projectModelStore].setProject({ key: projectKey });
      }
    },
    updateOpenContent(payload, state) {
      if (!payload || state[storeName].openContent === payload) {
        this.setOpenContent();
        return;
      }

      this.setOpenContent(payload);
    },
  }),
});

export default aboutModel;
